<template>
  <div class="exam-system">
    <!-- 背景装饰 -->
    <div class="bg-decoration">
      <div class="bg-circle bg-circle-1"></div>
      <div class="bg-circle bg-circle-2"></div>
      <div class="bg-circle bg-circle-3"></div>
    </div>

    <!-- 浮动元素 -->
    <div class="floating-elements" ref="floatingContainer"></div>

    <!-- 导航栏 -->
    <nav class="navbar">
      <div class="nav-container">
        <router-link to="/" class="nav-logo">
          <div class="nav-logo-icon">CET</div>
          <div class="nav-logo-text">智能考试评价系统V1.0</div>
        </router-link>
        <ul class="nav-menu">
          <li 
            v-for="(item, index) in navItems" 
            :key="index"
            :class="['nav-item', { active: activeNav === index }]"
            @click="activeNav = index"
          >
            {{ item }}
          </li>
        </ul>
      </div>
    </nav>

    <!-- 英雄区域 -->
    <section class="hero-section">
      <div class="hero-container">
        <div class="hero-badge">
          <span class="hero-badge-dot"></span>
          <span>中国-东盟建筑工程技术专业智能考试平台</span>
        </div>
        
        <h1 class="hero-title">智能考试评价系统V1.0</h1>
        <p class="hero-subtitle">基于AI的建筑工程技术专业考试智能化管理平台，支持多语言、多模态题型，全面提升考试效率与公平性</p>

        <div class="hero-stats">
          <div class="hero-stat" v-for="stat in heroStats" :key="stat.label">
            <div class="hero-stat-number" :ref="stat.ref">{{ stat.value }}</div>
            <div class="hero-stat-label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 主要内容 -->
    <main class="main-content">
      <!-- 内容标签页 -->
      <div class="content-tabs">
        <button 
          v-for="tab in tabs" 
          :key="tab.id"
          :class="['tab-button', { active: activeTab === tab.id }]"
          @click="activeTab = tab.id"
        >
          <span>{{ tab.icon }}</span>
          <span>{{ tab.label }}</span>
        </button>
      </div>

      <!-- 数据看板标签页 -->
      <div class="tab-content" :class="{ active: activeTab === 'dashboard' }">
        <div class="dashboard-grid">
          <div 
            v-for="card in dashboardCards" 
            :key="card.title"
            class="dashboard-card"
            @mouseenter="handleCardHover"
            @mouseleave="handleCardLeave"
          >
            <div class="dashboard-header">
              <div class="dashboard-title">
                <span class="dashboard-icon">{{ card.icon }}</span>
                {{ card.title }}
              </div>
            </div>
            <div class="dashboard-number">{{ card.value }}</div>
            <div class="dashboard-label">{{ card.label }}</div>
            <div :class="['dashboard-trend', card.trendClass]">
              <span>{{ card.trendIcon }}</span>
              <span>{{ card.trendText }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 考试管理标签页 -->
      <div class="tab-content" :class="{ active: activeTab === 'exam-management' }">
        <div class="exam-system-section">
          <div class="system-header">
            <h2 class="system-title">
              考试管理中心
              <span class="version-badge">V1.0</span>
            </h2>
            <p class="system-subtitle">全面管理建筑工程技术专业考试，支持多终端、多规则配置</p>
          </div>

          <div class="quick-actions">
            <div 
              v-for="action in quickActions" 
              :key="action.title"
              class="action-card"
              @click="handleActionClick(action)"
            >
              <div class="action-icon">{{ action.icon }}</div>
              <div class="action-text">
                <div class="action-title">{{ action.title }}</div>
                <div class="action-subtitle">{{ action.subtitle }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="features-grid">
          <div 
            v-for="feature in examFeatures" 
            :key="feature.title"
            class="feature-card"
          >
            <div class="feature-icon">{{ feature.icon }}</div>
            <h3 class="feature-title">{{ feature.title }}</h3>
            <p class="feature-description">{{ feature.description }}</p>
            <span :class="['feature-tag', feature.tagClass]">{{ feature.tag }}</span>
          </div>
        </div>
      </div>

      <!-- 智能组卷标签页 -->
      <div class="tab-content" :class="{ active: activeTab === 'question-bank' }">
        <div class="exam-system-section">
          <div class="system-header">
            <h2 class="system-title">智能组卷系统</h2>
            <p class="system-subtitle">基于知识点和难度的智能化试卷生成，支持多模态题型</p>
          </div>

          <div class="quick-actions">
            <div 
              v-for="action in questionBankActions" 
              :key="action.title"
              class="action-card"
              @click="handleActionClick(action)"
            >
              <div class="action-icon">{{ action.icon }}</div>
              <div class="action-text">
                <div class="action-title">{{ action.title }}</div>
                <div class="action-subtitle">{{ action.subtitle }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="features-grid">
          <div 
            v-for="feature in questionBankFeatures" 
            :key="feature.title"
            class="feature-card"
          >
            <div class="feature-icon">{{ feature.icon }}</div>
            <h3 class="feature-title">{{ feature.title }}</h3>
            <p class="feature-description">{{ feature.description }}</p>
            <span :class="['feature-tag', feature.tagClass]">{{ feature.tag }}</span>
          </div>
        </div>
      </div>

      <!-- 智能评分标签页 -->
      <div class="tab-content" :class="{ active: activeTab === 'evaluation' }">
        <div class="exam-system-section">
          <div class="system-header">
            <h2 class="system-title">智能评分引擎</h2>
            <p class="system-subtitle">基于AI的智能评分系统，支持开放性设计题语义分析</p>
          </div>

          <div class="quick-actions">
            <div 
              v-for="action in evaluationActions" 
              :key="action.title"
              class="action-card"
              @click="handleActionClick(action)"
            >
              <div class="action-icon">{{ action.icon }}</div>
              <div class="action-text">
                <div class="action-title">{{ action.title }}</div>
                <div class="action-subtitle">{{ action.subtitle }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="features-grid">
          <div 
            v-for="feature in evaluationFeatures" 
            :key="feature.title"
            class="feature-card"
          >
            <div class="feature-icon">{{ feature.icon }}</div>
            <h3 class="feature-title">{{ feature.title }}</h3>
            <p class="feature-description">{{ feature.description }}</p>
            <span :class="['feature-tag', feature.tagClass]">{{ feature.tag }}</span>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'ExamSystemHomepage',
  data() {
    return {
      activeNav: 0,
      activeTab: 'dashboard',
      navItems: ['考试管理', '题库管理', '数据分析', '系统设置'],
      tabs: [
        { id: 'dashboard', icon: '📊', label: '数据看板' },
        { id: 'exam-management', icon: '📝', label: '考试管理' },
        { id: 'question-bank', icon: '📚', label: '智能组卷' },
        { id: 'evaluation', icon: '🔍', label: '智能评分' }
      ],
      heroStats: [
        { label: '活跃考试', value: 0, target: 13, ref: 'activeExams' },
        { label: '题库总量', value: 0, target: 5247, ref: 'questionBank' },
        { label: '在线考生', value: 0, target: 2856, ref: 'examinees' },
        { label: '已上传试卷', value: 0, target: 1238, ref: 'uploadedPapers' }
      ],
      dashboardCards: [
        {
          icon: '👥',
          title: '参考率',
          value: '96.8%',
          label: '本月考试参考率',
          trendIcon: '↗',
          trendText: '+2.3% 较上月',
          trendClass: 'trend-up'
        },
        {
          icon: '✅',
          title: '通过率',
          value: '78.5%',
          label: '考试平均通过率',
          trendIcon: '↗',
          trendText: '+5.2% 较上月',
          trendClass: 'trend-up'
        },
        {
          icon: '❌',
          title: '题目错误率',
          value: '12.7%',
          label: '平均题目错误率',
          trendIcon: '↘',
          trendText: '-1.8% 较上月',
          trendClass: 'trend-down'
        },
        {
          icon: '⏱️',
          title: '平均用时',
          value: '47',
          label: '分钟 / 考试',
          trendIcon: '↘',
          trendText: '-3.5分钟 较上月',
          trendClass: 'trend-down'
        },
        {
          icon: '🌍',
          title: '多语言使用',
          value: '67%',
          label: '中英双语考试占比',
          trendIcon: '↗',
          trendText: '+8.1% 较上月',
          trendClass: 'trend-up'
        },
        {
          icon: '🔄',
          title: '断点续考',
          value: '234',
          label: '次断点续考恢复',
          trendIcon: '↗',
          trendText: '恢复成功率99.2%',
          trendClass: 'trend-up'
        }
      ],
      quickActions: [
        { icon: '📤', title: '上传试题试卷', subtitle: '支持Word/Excel格式导入' },
        { icon: '📋', title: '创建新考试', subtitle: '支持多种题型配置' },
        { icon: '⚙️', title: '考试规则设置', subtitle: '限时、禁止返回等策略' },
        { icon: '🌐', title: '多终端适配管理', subtitle: '跨平台兼容性测试' }
      ],
      examFeatures: [
        {
          icon: '🔧',
          title: '考试规则自定义',
          description: '允许院校灵活设置考试策略，包括限时答题、禁止返回修改、随机出题顺序等多种考试规则配置',
          tag: '灵活配置',
          tagClass: 'primary'
        },
        {
          icon: '💻',
          title: '多终端适配支持',
          description: '完美兼容Chrome/Firefox/Safari/Edge及移动端浏览器，提供全平台无缝考试体验',
          tag: '跨平台',
          tagClass: ''
        },
        {
          icon: '🔄',
          title: '断点续考机制',
          description: '网络中断后自动保存进度，恢复连接后可从断点处继续答题，确保考试连续性',
          tag: '高可靠',
          tagClass: 'warning'
        }
      ],
      questionBankActions: [
        { icon: '📤', title: '上传试题文件', subtitle: 'Word/Excel格式批量导入' },
        { icon: '🎯', title: '按知识点抽题', subtitle: '依据教学大纲智能筛选' },
        { icon: '📊', title: '难度分级组卷', subtitle: '简单/中等/困难智能配比' },
        { icon: '⚖️', title: '题型比例控制', subtitle: '单选题、计算题占比自定义' },
        { icon: '🔍', title: '试卷查重检查', subtitle: '避免知识点重复超过两次' },
        { icon: '🏢', title: '企业真题导入', subtitle: '行业龙头企业题库' }
      ],
      questionBankFeatures: [
        {
          icon: '📤',
          title: '试题文件上传',
          description: '支持Word(.docx)和Excel(.xlsx)格式的试题批量导入，自动解析题目内容、答案和知识点标签',
          tag: '批量导入',
          tagClass: ''
        },
        {
          icon: '📝',
          title: '智能按知识点抽题',
          description: '系统可依据教学大纲要求，自动筛选指定知识点的试题，确保考试内容与教学目标一致',
          tag: '精准匹配',
          tagClass: ''
        },
        {
          icon: '⚡',
          title: '难度分级智能组卷',
          description: '支持设置试卷整体难度（简单/中等/困难），系统自动调配不同难度题目的分布比例',
          tag: '智能调配',
          tagClass: 'primary'
        },
        {
          icon: '🎨',
          title: '多模态试题支持',
          description: '可解析含图片、文档、表格的复合型试题，支持BIM模型、施工图纸等专业素材',
          tag: '多媒体',
          tagClass: 'warning'
        },
        {
          icon: '🌍',
          title: '多语言试题库',
          description: '支持中英语言试题的同源自动翻译，满足国际化教学需求',
          tag: '国际化',
          tagClass: ''
        },
        {
          icon: '🏢',
          title: '企业真题嵌入',
          description: '可接入行业龙头企业提供的实际工程问题库，如结构节点设计真题等',
          tag: '产教融合',
          tagClass: 'danger'
        },
        {
          icon: '✅',
          title: '试卷合规性检查',
          description: '自动验证组卷结果是否符合总分要求，确保试卷质量和规范性',
          tag: '质量保障',
          tagClass: ''
        },
        {
          icon: '🔄',
          title: '文件格式智能解析',
          description: '自动识别Word文档中的题目结构和Excel表格中的题库格式，支持多种导入模板',
          tag: '智能解析',
          tagClass: 'primary'
        }
      ],
      evaluationActions: [
        { icon: '🧠', title: '语义分析评分', subtitle: '开放性设计题智能识别' },
        { icon: '📐', title: '结构化解题步骤', subtitle: '计算题步骤识别评分' },
        { icon: '📊', title: '评分结果分析', subtitle: '多维度评分报告生成' },
        { icon: '🔍', title: '答题质量监控', subtitle: '实时检测答题异常' }
      ],
      evaluationFeatures: [
        {
          icon: '🤖',
          title: '智能评分引擎',
          description: '对开放性设计题采用语义分析+结构化解题步骤识别，提供客观准确的评分结果',
          tag: 'AI驱动',
          tagClass: ''
        },
        {
          icon: '📈',
          title: '考试数据看板',
          description: '为管理员提供参考率、通过率、题目错误率等12项核心指标的可视化展示',
          tag: '数据洞察',
          tagClass: 'primary'
        },
        {
          icon: '⚡',
          title: '实时评分反馈',
          description: '支持即时评分和延时评分模式，提供详细的评分解析和改进建议',
          tag: '即时反馈',
          tagClass: 'warning'
        }
      ],
      floatingInterval: null
    }
  },
  mounted() {
    this.createFloatingElements()
    this.animateNumbers()
    this.initIntersectionObserver()
  },
  beforeDestroy() {
    if (this.floatingInterval) {
      clearInterval(this.floatingInterval)
    }
  },
  methods: {
    createFloatingElements() {
      const container = this.$refs.floatingContainer
      
      const createElement = () => {
        const element = document.createElement('div')
        element.className = 'floating-element'
        element.style.left = Math.random() * 100 + '%'
        element.style.width = element.style.height = Math.random() * 40 + 20 + 'px'
        element.style.animationDelay = Math.random() * 20 + 's'
        element.style.animationDuration = Math.random() * 20 + 20 + 's'
        container.appendChild(element)
        
        setTimeout(() => {
          if (element.parentNode) {
            element.remove()
          }
        }, 40000)
      }
      
      // 创建初始元素
      for (let i = 0; i < 10; i++) {
        setTimeout(createElement, i * 1000)
      }
      
      // 持续创建元素
      this.floatingInterval = setInterval(createElement, 4000)
    },
    animateNumbers() {
      this.heroStats.forEach(stat => {
        const element = this.$refs[stat.ref]?.[0]
        if (element) {
          let current = 0
          const increment = stat.target / 50
          const timer = setInterval(() => {
            current += increment
            if (current >= stat.target) {
              current = stat.target
              clearInterval(timer)
            }
            element.textContent = Math.floor(current).toLocaleString()
          }, 30)
        }
      })
    },
    initIntersectionObserver() {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1'
            entry.target.style.transform = 'translateY(0)'
          }
        })
      }, observerOptions)

      // 观察需要动画的元素
      this.$nextTick(() => {
        const animatedElements = this.$el.querySelectorAll('.dashboard-card, .feature-card, .action-card, .exam-system-section')
        animatedElements.forEach((el, index) => {
          el.style.opacity = '0'
          el.style.transform = 'translateY(40px)'
          el.style.transition = `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`
          observer.observe(el)
        })
      })
    },
    handleActionClick(action) {
      this.$emit('action-click', action)
      console.log('Action clicked:', action.title)
    },
    handleCardHover(e) {
      const card = e.currentTarget
      const rect = card.getBoundingClientRect()
      
      const handleMouseMove = (e) => {
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        
        const centerX = rect.width / 2
        const centerY = rect.height / 2
        
        const rotateX = (y - centerY) / centerY * -5
        const rotateY = (x - centerX) / centerX * 5
        
        card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-4px)`
      }
      
      card.addEventListener('mousemove', handleMouseMove)
      card._mouseMoveHandler = handleMouseMove
    },
    handleCardLeave(e) {
      const card = e.currentTarget
      card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateY(0)'
      
      if (card._mouseMoveHandler) {
        card.removeEventListener('mousemove', card._mouseMoveHandler)
        delete card._mouseMoveHandler
      }
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.exam-system {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  background: linear-gradient(135deg, #EBF5FF 0%, #F0F9FF 50%, #FFF7ED 100%);
  color: #111827;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}

/* 背景装饰 */
.bg-decoration {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: -1;
  overflow: hidden;
}

.bg-circle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.03;
}

.bg-circle-1 {
  width: 600px;
  height: 600px;
  background: #2E5CE6;
  top: -300px;
  right: -200px;
}

.bg-circle-2 {
  width: 400px;
  height: 400px;
  background: #D97706;
  bottom: -200px;
  left: -100px;
}

.bg-circle-3 {
  width: 300px;
  height: 300px;
  background: #DC2626;
  top: 50%;
  left: 10%;
}

/* 导航栏 */
.navbar {
  background: white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  padding: 16px 0;
  position: sticky;
  top: 0;
  z-index: 100;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
}

.nav-logo-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #DC2626 0%, #D97706 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 800;
  font-size: 18px;
}

.nav-logo-text {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.nav-menu {
  display: flex;
  gap: 24px;
  list-style: none;
}

.nav-item {
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;
}

.nav-item:hover {
  background: #F3F4F6;
}

.nav-item.active {
  background: #2E5CE6;
  color: white;
}

/* 英雄区域 */
.hero-section {
  padding: 40px 0 60px;
  text-align: center;
  position: relative;
}

.hero-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 8px 20px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  color: #2E5CE6;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
}

.hero-badge-dot {
  width: 8px;
  height: 8px;
  background: #10B981;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.8); }
}

.hero-title {
  font-size: 56px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #DC2626 0%, #2E5CE6 50%, #D97706 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 20px;
  color: #6B7280;
  max-width: 700px;
  margin: 0 auto 40px;
  line-height: 1.6;
}

.hero-stats {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-top: 40px;
}

.hero-stat {
  text-align: center;
}

.hero-stat-number {
  font-size: 32px;
  font-weight: 800;
  background: linear-gradient(135deg, #DC2626 0%, #2E5CE6 50%, #D97706 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 8px;
}

.hero-stat-label {
  color: #6B7280;
  font-size: 14px;
  font-weight: 500;
}

/* 主要内容 */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px 80px;
}

.content-tabs {
  display: flex;
  background: white;
  border-radius: 24px;
  padding: 8px;
  margin-bottom: 32px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  gap: 4px;
}

.tab-button {
  flex: 1;
  padding: 16px 24px;
  border: none;
  background: transparent;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #6B7280;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.tab-button.active {
  background: linear-gradient(135deg, #2E5CE6 0%, #D97706 100%);
  color: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.tab-button:hover:not(.active) {
  background: #F3F4F6;
  color: #111827;
}

/* 标签页内容 */
.tab-content {
  display: none;
}

.tab-content.active {
  display: block;
}

/* 数据看板网格 */
.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.dashboard-card {
  background: white;
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #E5E7EB;
  position: relative;
  overflow: hidden;
}

.dashboard-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #DC2626 0%, #2E5CE6 50%, #D97706 100%);
  transform: scaleX(0);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dashboard-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.dashboard-card:hover::before {
  transform: scaleX(1);
}

.dashboard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.dashboard-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dashboard-icon {
  font-size: 24px;
}

.dashboard-number {
  font-size: 36px;
  font-weight: 800;
  background: linear-gradient(135deg, #DC2626 0%, #2E5CE6 50%, #D97706 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 8px;
}

.dashboard-label {
  color: #6B7280;
  font-size: 14px;
}

.dashboard-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  margin-top: 12px;
}

.trend-up {
  color: #10B981;
}

.trend-down {
  color: #EF4444;
}

/* 功能卡片 */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 24px;
}

.feature-card {
  background: white;
  border-radius: 24px;
  padding: 32px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #E5E7EB;
  position: relative;
  overflow: hidden;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, #2E5CE6 0%, #D97706 100%);
  border-radius: 24px;
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: -1;
}

.feature-card:hover {
  transform: translateY(-4px);
  background: white;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.feature-card:hover::before {
  opacity: 1;
}

.feature-icon {
  width: 64px;
  height: 64px;
  background: #F3F4F6;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin-bottom: 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.feature-card:hover .feature-icon {
  background: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.feature-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #111827;
}

.feature-description {
  font-size: 16px;
  color: #6B7280;
  line-height: 1.6;
  margin-bottom: 16px;
}

.feature-tag {
  display: inline-block;
  background: #10B981;
  color: white;
  padding: 6px 16px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
}

.feature-tag.warning {
  background: #F59E0B;
}

.feature-tag.danger {
  background: #EF4444;
}

.feature-tag.primary {
  background: #2E5CE6;
}

/* 考试系统区域 */
.exam-system-section {
  background: white;
  border-radius: 32px;
  padding: 48px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  margin-bottom: 32px;
}

.system-header {
  text-align: center;
  margin-bottom: 48px;
}

.system-title {
  font-size: 40px;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #DC2626 0%, #2E5CE6 50%, #D97706 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.system-subtitle {
  font-size: 20px;
  color: #6B7280;
  font-weight: 500;
}

.version-badge {
  display: inline-block;
  background: linear-gradient(135deg, #2E5CE6 0%, #D97706 100%);
  color: white;
  padding: 6px 20px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 700;
  margin-left: 12px;
}

/* 快速操作 */
.quick-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 48px;
}

.action-card {
  background: #F9FAFB;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #E5E7EB;
  position: relative;
  overflow: hidden;
}

.action-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #2E5CE6 0%, #D97706 100%);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.action-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  background: white;
}

.action-card:hover::before {
  opacity: 0.05;
}

.action-icon {
  width: 48px;
  height: 48px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.action-text {
  flex: 1;
  position: relative;
  z-index: 1;
}

.action-title {
  font-weight: 600;
  font-size: 16px;
  color: #111827;
  line-height: 1.4;
}

.action-subtitle {
  font-size: 14px;
  color: #6B7280;
  margin-top: 4px;
}

/* 浮动元素 */
.floating-elements {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: -1;
  overflow: hidden;
}

.floating-element {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, #2E5CE6 0%, #D97706 100%);
  opacity: 0.03;
  animation: float-element 20s infinite linear;
}

@keyframes float-element {
  0% {
    transform: translateY(100vh) rotate(0deg);
  }
  100% {
    transform: translateY(-100px) rotate(360deg);
  }
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .hero-title {
    font-size: 48px;
  }

  .nav-menu {
    display: none;
  }

  .hero-stats {
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 36px;
  }

  .system-title {
    font-size: 32px;
  }

  .dashboard-grid,
  .features-grid,
  .quick-actions {
    grid-template-columns: 1fr;
  }

  .hero-stats {
    flex-direction: column;
    gap: 16px;
  }

  .content-tabs {
    flex-direction: column;
    gap: 8px;
  }

  .exam-system-section {
    padding: 24px;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 28px;
  }

  .hero-subtitle {
    font-size: 16px;
  }
}
</style>